import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../redux/authSlice.js';
import { ROLE_PROFESSOR } from '../../constants/roles';

const GOLD = '#E0C281';

// Bouton profil (avatar + nom + rôle) qui ouvre un dropdown contenant "Déconnexion".
// Utilisé dans la sidebar desktop et le drawer mobile, pour tous les rôles —
// seule la ligne de rôle sous le nom d'utilisateur diffère (visible uniquement pour un professeur).
const UserMenuButton = ({ collapsed = false }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useSelector((state) => state.auth.user);
    const isProfessor = user?.role === ROLE_PROFESSOR;
    const username = user?.username || '';
    const letter = username.charAt(0).toUpperCase() || '?';

    const [open, setOpen] = useState(false);
    const [hovered, setHovered] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = () => {
        setOpen(false);
        dispatch(logout());
        navigate('/connexion');
    };

    return (
        <div ref={containerRef} style={{ position: 'relative' }}>
            <button
                onClick={() => setOpen((o) => !o)}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{
                    display: 'flex', alignItems: 'center',
                    justifyContent: collapsed ? 'center' : 'space-between',
                    gap: collapsed ? 0 : 10,
                    width: '100%',
                    padding: '8px 10px', borderRadius: 10,
                    background: (open || hovered) ? 'rgba(0,0,0,0.2)' : 'transparent',
                    border: 'none', cursor: 'pointer',
                    transition: 'background 0.2s ease, gap 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: collapsed ? 0 : 10, minWidth: 0 }}>
                    <div style={{
                        width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                        background: `linear-gradient(155deg, ${GOLD}33, ${GOLD}14)`,
                        border: `1px solid ${GOLD}55`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 14, fontWeight: 700, color: GOLD,
                    }}>{letter}</div>

                    <div style={{
                        textAlign: 'left', minWidth: 0,
                        whiteSpace: 'nowrap', overflow: 'hidden',
                        maxWidth: collapsed ? 0 : 160,
                        opacity: collapsed ? 0 : 1,
                        transition: 'max-width 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.15s ease',
                    }}>
                        <div style={{
                            fontSize: 13, fontWeight: 600, color: '#F2F5FB',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{username}</div>
                        {isProfessor && (
                            <div style={{ fontSize: 11, color: '#5C6B88' }}>Professeur</div>
                        )}
                    </div>
                </div>

                {!collapsed && (
                    <svg width={24} height={24} viewBox="0 0 22 22" fill="none" style={{ flexShrink: 0, color: '#5C6B88' }}>
                        <path d="M7 10l4-4 4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M7 13l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                )}
            </button>

            {open && (
                <div style={{
                    position: 'absolute', bottom: '100%', left: 0, right: 0, marginBottom: 8,
                    width: '100%', minWidth: 180, zIndex: 50,
                    background: '#14213D',
                    border: '1px solid rgba(255,255,255,0.07)',
                    borderRadius: 12, padding: 6,
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05), 0 12px 32px -16px rgba(0,0,0,0.6)',
                }}>
                    <button onClick={handleLogout} style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        width: '100%', padding: '9px 10px', borderRadius: 8,
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        color: '#FF7A78', fontSize: 13.5, fontWeight: 500,
                    }}>
                        <svg width={18} height={18} viewBox="0 0 22 22" fill="none" style={{ flexShrink: 0 }}>
                            <path d="M8 11h10M14 7l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M14 4H5a1 1 0 00-1 1v12a1 1 0 001 1h9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                        </svg>
                        Déconnexion
                    </button>
                </div>
            )}
        </div>
    );
};

export default UserMenuButton;
